import { Colors, Text } from '@amelio/ui-library';
import { useRef } from 'react';

import { RecapSections } from './RecapSections';
import { RetryButton } from './RetryButton';

import type { MeetingRecapRetry, MeetingTranscriptLabels } from '../types';
import type { MeetingRecap } from '@/types/review/PostReviewMeeting';

import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

const RecapSkeleton = () => {
    return (
        <Skeleton>
            <div className={'space-y-3'}>
                <Bone className={'h-4 w-full'} />
                <Bone className={'h-4 w-5/6'} />
                <Bone className={'h-24 w-full'} />
            </div>
        </Skeleton>
    );
};

interface RecapStatusZoneProps {
    recap: MeetingRecap | null;
    isWriting: boolean;
    retry: MeetingRecapRetry;
    labels: MeetingTranscriptLabels;
    disabled: boolean;
}

/** Nothing beside a recap; otherwise, whether one is being written or is unavailable for now. */
const recapStatusMessage = ({
    recap,
    isWriting,
    retry,
}: Pick<RecapStatusZoneProps, 'recap' | 'isWriting' | 'retry'>) => {
    if (recap) {
        return null;
    }
    return isWriting ? retry.pendingMessage : retry.unavailableMessage;
};

/**
 * The recap zone of a journey that can ask for the recap again. One status region stays mounted through
 * every state and only its words change, so "being prepared" turning into "unavailable" is announced; a
 * region mounted together with its text often is not. The recap and the retry sit outside it: the recap
 * is to be read rather than recited, and a live region recites everything it holds, a button included.
 */
export const RecapStatusZone = ({
    recap,
    isWriting,
    retry,
    labels,
    disabled,
}: RecapStatusZoneProps) => {
    const planLock = usePlanLock();
    const statusRef = useRef<HTMLDivElement>(null);
    const message = recapStatusMessage({ recap, isWriting, retry });

    return (
        <>
            <div className={'flex flex-col gap-3'}>
                <div
                    ref={statusRef}
                    role={'status'}
                    tabIndex={-1}
                    className={'flex flex-col gap-3 focus:outline-none'}
                >
                    {message && (
                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                        >
                            {message}
                        </Text>
                    )}
                    {isWriting && <RecapSkeleton />}
                </div>

                {!recap && !isWriting && (
                    <RetryButton
                        label={retry.retryLabel}
                        onRetry={planLock.isLocked ? planLock.onLocked : retry.onRetry}
                        statusRef={statusRef}
                        disabled={!planLock.isLocked && disabled}
                        dataTestId={'retry-recap-button'}
                        leftIcon={planLock.isLocked ? <PlanLockIcon /> : undefined}
                    />
                )}
            </div>

            {recap && (
                <RecapSections
                    recap={recap}
                    labels={labels}
                />
            )}
        </>
    );
};
